import { action, onConnect } from '@reatom/framework'

import { Array, AsyncStateN, pipe, Result } from '@blade/std-lib'
import { cmpByDate } from '@root/entities/holders-charts/holders-chart-entry'
import * as HoldersCharts from '@root/entities/holders-charts/holders-charts'
import * as Network from '@root/entities/network/model/network'
import { holderChartUpdateEvent } from '@root/features/realtime/reatom-websocket-main'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { ApiError, apiRequest } from '@root/shared/api'
import { reatomRequest } from '@root/shared/lib'

import type { PairAddress } from '@root/entities/pair'

interface HoldersChartsRequestActionParams {
  network: Network.Network
  pairAddress: PairAddress
}

export const holdersChartsRequestAction = action(
  async (ctx, params: HoldersChartsRequestActionParams) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.services.servicesMakerHoldersChartList, [
        {
          network: Network.toDto(params.network),
          pairAddress: params.pairAddress,
        },
      ]),
    )

    const mappedResult = result.pipe(
      Result.map((value) => HoldersCharts.fromDto(value)),
      Result.mapError(ApiError.fallbackToUnknown),
    )

    return mappedResult
  },
  'holdersChartsRequestAction',
)

export const reatomHoldersCharts = (
  params: HoldersChartsRequestActionParams,
) => {
  // eslint-disable-next-line @reatom/unit-naming-rule
  const requestStateAtom = reatomRequest({
    execAction: holdersChartsRequestAction,
    prepare: () => params,
  })

  onConnect(requestStateAtom, (ctx) => {
    const dependencies = ctx.get(dependenciesAtom)

    dependencies.mainWebsocket.holderChartSubscribeAction(ctx, {
      network: params.network,
      pairAddress: params.pairAddress,
    })
    requestStateAtom.execute(ctx)

    const unsubscribeChartUpdateEvent = holderChartUpdateEvent.onCall(
      (ctx, update) => {
        const requestState = ctx.get(requestStateAtom)

        if (
          update.network !== params.network ||
          update.pairAddress !== params.pairAddress
        ) {
          return
        }

        const newState = AsyncStateN.map(requestState, (charts) => {
          const newCharts: HoldersCharts.HoldersCharts = {
            poolCreatedAt: charts.poolCreatedAt,
            holdersCountChart: charts.holdersCountChart,
            top10HoldingChart: charts.top10HoldingChart,
            insidersHoldingChart: charts.insidersHoldingChart,
            bundlersHoldingChart: charts.bundlersHoldingChart,
            snipersHoldingChart: charts.snipersHoldingChart,
          }
          if (update.holdersCount) {
            newCharts.holdersCountChart = pipe(
              newCharts.holdersCountChart,
              Array.append({
                date: update.holdersCount.timestamp,
                value: update.holdersCount.value,
              }),
              Array.sort(cmpByDate),
            )
          }

          if (update.top10HoldersPercentage) {
            newCharts.top10HoldingChart = pipe(
              newCharts.top10HoldingChart,
              Array.append({
                date: update.top10HoldersPercentage.timestamp,
                value: update.top10HoldersPercentage.value,
              }),
              Array.sort(cmpByDate),
            )
          }
          if (update.insidersHolding) {
            newCharts.insidersHoldingChart = pipe(
              newCharts.insidersHoldingChart,
              Array.append({
                date: update.insidersHolding.timestamp,
                value: update.insidersHolding.value,
              }),
              Array.sort(cmpByDate),
            )
          }
          if (update.bundlersHolding) {
            newCharts.bundlersHoldingChart = pipe(
              newCharts.bundlersHoldingChart,
              Array.append({
                date: update.bundlersHolding.timestamp,
                value: update.bundlersHolding.value,
              }),
              Array.sort(cmpByDate),
            )
          }
          if (update.snipersHolding) {
            newCharts.snipersHoldingChart = pipe(
              newCharts.snipersHoldingChart,
              Array.append({
                date: update.snipersHolding.timestamp,
                value: update.snipersHolding.value,
              }),
              Array.sort(cmpByDate),
            )
          }

          return newCharts
        })
        requestStateAtom(ctx, newState)
      },
    )

    return () => {
      unsubscribeChartUpdateEvent()
      dependencies.mainWebsocket.holderChartUnsubscribeAction(ctx, {
        network: params.network,
        pairAddress: params.pairAddress,
      })
    }
  })

  return { requestStateAtom }
}
